import { displayedText } from '../../model/helpers'
import { IScreeningCompareRow, TCompareStatus } from '../../model/types'
import {
  compareFieldText,
  compareStatusText,
  criterionText,
  IReviewText,
  notComparedReasonText,
  COMPETITOR_BAD_ACTOR_REVIEW_TEXT as TEXT,
  useReviewText,
} from '../translations'

import { TruncatedValue } from './TruncatedValue'

import styles from './ScreeningCompareTable.module.scss'

interface IScreeningCompareTableProps {
  accessibleName: string
  rows: readonly IScreeningCompareRow[]
}

const STATUS_CLASS: Record<TCompareStatus, string | undefined> = {
  matched: styles.matched,
  similar: styles.similar,
  different: undefined,
  'not-compared': styles.notCompared,
}

const hasStatusClass = (status: string): status is TCompareStatus =>
  Object.prototype.hasOwnProperty.call(STATUS_CLASS, status)

const statusClassOf = (status: string): string | undefined =>
  hasStatusClass(status) ? STATUS_CLASS[status] : undefined

export const ScreeningCompareTable = ({ accessibleName, rows }: IScreeningCompareTableProps) => {
  const text = useReviewText()
  const noValue = text(TEXT.noValue)

  const labelOrRaw = (label: IReviewText | null, raw: unknown) =>
    label === null ? displayedText(raw, noValue) : text(label)

  const fieldOf = (row: IScreeningCompareRow) => labelOrRaw(compareFieldText(row.field), row.field)

  const listedOf = (row: IScreeningCompareRow) => {
    const listedValue = displayedText(row.listedValue, noValue)

    return row.listedField === 'alternativeName'
      ? `${listedValue} (${text(TEXT.compareAlternativeName)})`
      : listedValue
  }

  const submittedOf = (row: IScreeningCompareRow) => {
    const submittedValue = displayedText(row.submittedValue, noValue)

    if (row.field !== 'name') {
      return submittedValue
    }

    switch (row.submittedField) {
      case 'legalName':
        return `${submittedValue} (${text(TEXT.compareLegalName)})`
      case 'tradingName':
        return `${submittedValue} (${text(TEXT.compareTradingName)})`
      default:
        return submittedValue
    }
  }

  const notComparedReasonOf = (row: IScreeningCompareRow): IReviewText | null =>
    typeof row.reason === 'string' ? notComparedReasonText(row.reason) : null

  const statusOf = (row: IScreeningCompareRow) => {
    const label = labelOrRaw(compareStatusText(row.status), row.status)
    const reason = row.status === 'not-compared' ? notComparedReasonOf(row) : null
    const status = reason === null ? label : `${label}: ${text(reason)}`
    const criterion =
      typeof row.criterion === 'string'
        ? `: ${labelOrRaw(criterionText(row.criterion), row.criterion)}`
        : ''
    const score =
      typeof row.score === 'number' ? ` (${text(TEXT.scoreValue, { score: row.score })})` : ''

    return `${status}${criterion}${score}`
  }

  return (
    <table className={styles.table} aria-label={accessibleName}>
      <thead>
        <tr>
          <th scope="col">{text(TEXT.compareColField)}</th>
          <th scope="col">{text(TEXT.compareColListed)}</th>
          <th scope="col">{text(TEXT.compareColSubmitted)}</th>
          <th scope="col">{text(TEXT.compareColStatus)}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index} className={statusClassOf(row.status)}>
            <th scope="row">{fieldOf(row)}</th>
            <td>
              <TruncatedValue value={listedOf(row)} />
            </td>
            <td>
              <TruncatedValue value={submittedOf(row)} />
            </td>
            <td>{statusOf(row)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
